import { LitElement, html, TemplateResult, css, unsafeCSS } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { property, state, query } from 'lit/decorators.js';
import { localized } from '@lit/localize';
import mainStyles from './styles/main.scss?inline';
import { updateLocale } from './localization/config';
import { emitEvent, emitError, emitWarning } from './utils/form-associated';
import type { RoundtripStageContent } from './types/roundtrip-stage-content.types.js';
import { fetchRoundtripStageContent } from './services/roundtrip-stage-content.service.js';
import type {
    RoundtripStageMiddlewareRequest,
    RoundtripStageMiddlewareResponse,
} from './types/roundtrip-stage-middleware.types.js';
import { renderIntro } from './templates/intro.js';
import { renderRichText } from './templates/rich-text.js';
import { type BookingRequestVariant, type ModalContent, type OfferRequestVariant } from './templates/dialog-body.js';
import { renderDialog, type DialogTemplateOptions } from './templates/dialog.js';
import { renderOfferPanel, type OfferPanelTemplateOptions } from './templates/offer-panel.js';
import { interceptChildEvents, setInitialUri, triggerSchemaJsonEvent } from './services/schemaOrgJsonLd.service';

type CalendarActionDetail = { startDate?: string; [key: string]: unknown } | 'noDateSelected';

type ContentState = 'loading' | 'loaded' | 'empty';

/**
 * Roundtrip Stage – orchestrating container for the roundtrip detail page.
 * Receives all configuration via HTML attributes and distributes them to the
 * child MFEs: `der-tm-roundtrip-gallery` and `der-tm-roundtrip-offer-panel`.
 */
@localized()
export class RoundtripStage extends LitElement {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    static formAssociated = true;

    @property({ type: String, attribute: 'lang' })
    public lang = '';

    @property({ type: String, attribute: 'currency' })
    public currency = '';

    @property({ type: String, attribute: 'brand' })
    public brand = '';

    @property({ type: String, attribute: 'tenant' })
    public tenant = '';

    @property({
        type: Object,
        attribute: 'additional-tracking-data',
        converter: {
            fromAttribute: (value: string | null) => {
                if (!value) return {};
                try {
                    return JSON.parse(value);
                } catch {
                    return {};
                }
            },
            toAttribute: (value: unknown) => (value == null ? null : JSON.stringify(value)),
        },
    })
    public additionalTrackingData = {};

    @property({ type: String, attribute: 'id' })
    public stageId = '';

    @property({ type: String, attribute: 'identifier' })
    public identifier = '';

    @property({ type: String, attribute: 'component-title' })
    public componentTitle = '';

    @property({ type: String, attribute: 'phone-number' })
    public phoneNumber = '';

    @property({ type: String, attribute: 'opportunity-owning-team' })
    public opportunityOwningTeam = '';

    @property({ type: String, attribute: 'opportunity-product-brand' })
    public opportunityProductBrand = '';

    @property({ type: String, attribute: 'opportunity-source' })
    public opportunitySource = '948150003';

    @property({ type: String, attribute: 'opportunity-type' })
    public opportunityType = 'Offer';

    @property({ type: String, attribute: 'booking-request-variant' })
    public bookingRequestVariant: BookingRequestVariant = 'default';

    @property({ type: String, attribute: 'offer-request-variant' })
    public offerRequestVariant: OfferRequestVariant = 'default';

    @property({ type: Boolean, attribute: 'offer-request-description-optional' })
    public offerRequestDescriptionOptional = false;

    @property({ type: String, attribute: 'newsletter' })
    public newsletter = '';

    @property({ type: Boolean, attribute: 'enable-recap' })
    public enableRecap = false;

    @property({ type: Number, attribute: 'booking-day-offset' })
    public bookingDayOffset = 3;

    @property({ type: Boolean, attribute: 'show-operator' })
    public showOperator = true;

    @property({ type: Number, attribute: 'gallery-image-count' })
    public galleryImageCount: number | undefined = undefined;

    @property({
        type: String,
        attribute: 'travel-context',
        converter: {
            fromAttribute: (value: string | null) => {
                const trimmed = (value ?? '').trim();
                return trimmed.length > 0 ? trimmed : 'ROUNDTRIPS';
            },
        },
    })
    public travelContext = 'ROUNDTRIPS';

    @property({ type: String, attribute: 'intent' })
    public intent = '';

    @property({ type: String, attribute: 'facet_audience' })
    public facetAudience = '';

    @property({ type: String, attribute: 'facet_style' })
    public facetStyle = '';

    @property({ type: String, attribute: 'facet_motive' })
    public facetMotive = '';

    @state()
    private _stickyVisible = false;

    @state()
    private _isMobileViewport = false;

    @state()
    private _modalContent: ModalContent = 'calendar';

    @state()
    private _selectedOfferData: Record<string, unknown> | null = null;

    @state()
    private _stageContent: RoundtripStageContent = {
        pageTitle: '',
        componentTitle: '',
        introTitle: '',
    };

    @state()
    private _introState: ContentState = 'loading';

    private _backendFallbackPrice = '';

    private _backendDurationString = '';

    private _backendRoundtripTypes = '';

    private _backendRoundtripKey = '';

    private _backendOpportunityDestination = '';

    private _backendStartAndEndPoint = '';

    private _backendStartPoint = '';

    private _backendLanguages = '';

    private _backendGroupSize = '';

    private _backendBestTravelTime = '';

    private _isBodyScrollLocked = false;

    private _bodyOverflowBeforeDialog = '';

    private _bodyOverscrollBeforeDialog = '';

    private _ctaObserver: IntersectionObserver | null = null;

    private _galleryOverlayObserver: MutationObserver | null = null;

    private _observedSelector: string | null = null;

    private _observeRetryTimeoutId: number | null = null;

    // iOS-specific sticky fallback
    private _iosStickyIntervalId: number | null = null;

    private _stickyBarPortalEl: HTMLElement | null = null;

    private _removeSchemaEventInterceptor: (() => void) | null = null;

    @query('#der-tm-roundtrip-stage-dialog')
    private _dialog!: HTMLDialogElement;

    /**
     * Lit CSS is scoped to the Shadow DOM and no longer applies after an `appendChild` to `document.body`.
     * To ensure the sticky bar animation (transform/opacity/pointer-events) works correctly in the iOS portal,
     * we inject the required sticky bar rules globally once.
     */
    private _ensureStickyBarGlobalStyles(): void {
        if (!this._shouldUseStickyBarPortal()) return;

        const styleId = 'der-tm-roundtrip-stage__sticky-bar-global-styles';
        if (document.getElementById(styleId)) return;

        const styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
            .der-tm-roundtrip-stage__sticky-bar {
                position: fixed;
                bottom: 0;
                left: 0;
                right: 0;
                z-index: 100;
                transform: translateY(100%);
                pointer-events: none;
                background: var(--der-ui-color-background-general-default);
                border-radius: var(--der-ui-radius-XL) var(--der-ui-radius-XL) 0 0;
                box-shadow: var(--der-ui-box-shadow-bottom-sheet);
                padding: var(--der-ui-spacing-S);
                transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                opacity: 0;
                width: auto;
                box-sizing: border-box;
                padding-bottom: calc(var(--der-ui-spacing-S) + env(safe-area-inset-bottom));
            }

            .der-tm-roundtrip-stage__sticky-bar.is-visible {
                transform: translateY(0);
                pointer-events: auto;
                opacity: 1;
            }
        `.trim();

        document.head.appendChild(styleEl);
    }

    private readonly _handleTouchMove = (): void => {
        this._updateStickyVisibility();
    };

    private readonly _handleOrientationChange = (): void => {
        this._syncViewportLayout();
        this._observeActiveOfferPanel();
        this._updateStickyVisibility();
    };

    private _isIOS(): boolean {
        // iPadOS may report itself as "MacIntel" (desktop UA) with touch support.
        const hasClassicIOSUa = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isIpadOsDesktopUa = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        return (hasClassicIOSUa || isIpadOsDesktopUa) && !('MSStream' in window);
    }

    /**
     * iOS (whether Safari or iOS browser wrappers) uses WebKit and can show the
     * sticky/`position: fixed` rendering bug inside Shadow DOM.
     * Therefore, always enable the portal logic for all iOS user agents.
     * @returns {boolean} Whether the portal logic for the sticky bar should be used.
     */
    private _shouldUseStickyBarPortal(): boolean {
        return this._isIOS();
    }

    private readonly _handleResize = (): void => {
        this._syncViewportLayout();
        this._observeActiveOfferPanel();
        this._updateStickyVisibility();
        if (this._shouldUseStickyBarPortal()) {
            this._forceRepaintStickyBar();
            this._updateStickyBarPortal();
        }
    };

    private readonly _handleScroll = (): void => {
        this._updateStickyVisibility();
        if (this._shouldUseStickyBarPortal()) {
            this._forceRepaintStickyBar();
            this._updateStickyBarPortal();
        }
    };

    private readonly _handleVisibilityChange = (): void => {
        if (!document.hidden) {
            this._updateStickyVisibility();
            if (this._shouldUseStickyBarPortal()) {
                this._forceRepaintStickyBar();
                this._updateStickyBarPortal();
            }
        }
    };

    private readonly _handleOpenModal = (event: Event): void => {
        const actionEvent = event as CustomEvent<{ hasOffers?: boolean; offersCount?: number } | null>;
        const detail = actionEvent.detail;
        const hasOffers = Boolean(detail && typeof detail === 'object' && detail.hasOffers);

        this._selectedOfferData = null;
        this._modalContent = hasOffers ? 'calendar' : 'offer-request';
        this._openModal();
    };

    private _lockBodyScroll(): void {
        if (this._isBodyScrollLocked) return;
        this._bodyOverflowBeforeDialog = document.body.style.overflow;
        this._bodyOverscrollBeforeDialog = document.body.style.overscrollBehavior;
        document.body.style.overflow = 'hidden';
        document.body.style.overscrollBehavior = 'contain';
        this._isBodyScrollLocked = true;
    }

    private _unlockBodyScroll(): void {
        if (!this._isBodyScrollLocked) return;
        document.body.style.overflow = this._bodyOverflowBeforeDialog;
        document.body.style.overscrollBehavior = this._bodyOverscrollBeforeDialog;
        this._isBodyScrollLocked = false;
    }

    private _openModal(): void {
        if (!this._dialog) return;
        if (this._dialog.open) {
            this._setInitialDialogFocus();
            return;
        }
        this._lockBodyScroll();
        this._dialog.showModal();
        this._setInitialDialogFocus();
    }

    private _setInitialDialogFocus(): void {
        requestAnimationFrame(() => {
            const focusableSelectors = [
                'input:not(:disabled)',
                'button:not(:disabled)',
                'textarea:not(:disabled)',
                'select:not(:disabled)',
                'a[href]',
                '[tabindex]:not([tabindex="-1"])',
            ].join(',');

            const firstFocusable = this._dialog.querySelector(focusableSelectors) as HTMLElement | null;
            firstFocusable?.focus();
        });
    }

    private _scrollDialogBodyToTop(): void {
        requestAnimationFrame(() => {
            const dialogBody = this.renderRoot.querySelector<HTMLElement>('.der-tm-roundtrip-stage__dialog-body');
            dialogBody?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        });
    }

    private readonly _handleDialogClose = (): void => {
        this._unlockBodyScroll();
        this._modalContent = 'calendar';
        this._selectedOfferData = null;
    };

    private readonly _handleCloseModal = (): void => {
        if (this._dialog) {
            this._dialog.close();
        }
    };

    private readonly _handleBackToCalendar = (): void => {
        this._selectedOfferData = null;
        this._modalContent = 'calendar';
        this._setInitialDialogFocus();
    };

    private readonly _handleConfirmationClose = (): void => {
        this._handleCloseModal();
    };

    private readonly _handleOfferPanelCalendarAction = (event: Event): void => {
        const calendarActionEvent = event as CustomEvent<CalendarActionDetail>;
        const detail = calendarActionEvent.detail;

        if (
            detail &&
            typeof detail === 'object' &&
            typeof detail.startDate === 'string' &&
            detail.startDate.length > 0
        ) {
            this._selectedOfferData = detail;
            this._modalContent = 'booking-request';
            console.log('Calendar action with date selected:', detail.startDate);
            this._openModal();
            return;
        }
        this._selectedOfferData = null;
        this._modalContent = 'booking-request';
        this._openModal();
    };

    private readonly _handleStickyBarPriceBoxAction = (event: Event): void => {
        event.stopPropagation();
        this._handleOpenModal(event);
    };

    private readonly _handleStickyBarCalendarAction = (event: Event): void => {
        event.stopPropagation();
        this._handleOfferPanelCalendarAction(event);
    };

    /**
     * Initializes the component when connected to the DOM.
     * Sets up localization based on the configured language attribute.
     * @returns Promise that resolves after locale resources are loaded
     */
    async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._syncViewportLayout();
        this._removeSchemaEventInterceptor?.();
        this._removeSchemaEventInterceptor = interceptChildEvents(this);
        // Set up localization based on the configured language attribute
        const langAttribute = this.lang ?? 'de-CH';
        await updateLocale(langAttribute);
        this._emitSchemaJsonEvent();
    }

    /**
     * Reacts to relevant attribute/property changes and refreshes middleware content when needed.
     * @param changedProperties - Properties changed since the previous update cycle
     */
    override updated(changedProperties: Map<string, unknown>): void {
        super.updated(changedProperties);

        if (changedProperties.has('_modalContent')) {
            this._scrollDialogBodyToTop();
        }

        if (changedProperties.has('lang')) {
            void updateLocale(this.lang ?? 'de-CH');
        }

        const middlewareRelevantKeys = [
            'brand',
            'tenant',
            'lang',
            'currency',
            'identifier',
            'travelContext',
            'intent',
            'facetAudience',
        ];

        const shouldRefetchStageContent = middlewareRelevantKeys.some((key) => changedProperties.has(key));
        if (shouldRefetchStageContent) {
            void this._reloadStageContentAndEmitSchema();
        }
    }

    private _emitSchemaJsonEvent(): void {
        triggerSchemaJsonEvent(
            {
                uri: setInitialUri(this.identifier),
                data: {
                    name: this._stageContent.pageTitle,
                    departure: '',
                    destination: this._backendOpportunityDestination,
                    description: this._stageContent.introTitle,
                },
            },
            this
        );
    }

    private async _reloadStageContentAndEmitSchema(): Promise<void> {
        this._introState = 'loading';
        await this._loadStageContentFromMiddleware();
        this._emitSchemaJsonEvent();
    }

    private _stripHtml(input?: string): string | undefined {
        if (!input) {
            return undefined;
        }

        return input
            .replace(/<[^>]*>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    private _stripHtmlList(input?: string[]): string {
        return (input ?? [])
            .map((item) => this._stripHtml(item) ?? '')
            .filter((item) => item.length > 0)
            .join(', ');
    }

    private _formatDurationLabel(duration?: number): string {
        if (!duration || duration <= 0) {
            return '';
        }

        const dayLabel = this.lang?.toLowerCase().startsWith('fr') ? 'jours' : 'Tage';
        return `${duration} ${dayLabel}`;
    }

    private _mapMiddlewareToStageContent(response: RoundtripStageMiddlewareResponse): RoundtripStageContent {
        const overview = response.roundtripOverview;
        const offer = response.roundtripOffer;
        const durationStringFromBackend = this._stripHtml(offer?.durationString) ?? '';
        const bestTimeToTravelFromBackend = this._stripHtml(
            (offer as { bestTimeToTravel?: string } | undefined)?.bestTimeToTravel
        );

        this._backendFallbackPrice = offer?.price?.trim() ?? '';
        this._backendDurationString = durationStringFromBackend || this._formatDurationLabel(offer?.duration);
        this._backendRoundtripTypes = this._stripHtmlList(offer?.tourType);
        this._backendRoundtripKey = this._stripHtml(response.itineraryKey) ?? '';
        this._backendOpportunityDestination = this._stripHtml(offer?.destination) ?? '';
        this._backendStartAndEndPoint = this._stripHtml(offer?.startAndEndPoint) ?? '';
        this._backendStartPoint = this._stripHtml(offer?.departurePoint) ?? '';
        this._backendLanguages = this._stripHtml(offer?.language) ?? '';
        this._backendGroupSize = this._stripHtml(offer?.groupSize) ?? '';
        this._backendBestTravelTime = bestTimeToTravelFromBackend ?? '';
        return {
            pageTitle: this._stripHtml(overview?.productTitle) ?? '',
            componentTitle: this._stripHtml(overview?.productOneLiner) ?? '',
            introTitle: this._stripHtml(overview?.shortDescription) ?? '',
            richText: overview?.highlights,
        };
    }

    private _getMissingStageContentParams(): string[] {
        const requiredParams = {
            brand: this.brand,
            tenant: this.tenant,
            lang: this.lang,
            currency: this.currency,
            identifier: this.identifier,
            travelcontext: this.travelContext,
        };

        return Object.entries(requiredParams)
            .filter(([, value]) => !value?.trim())
            .map(([key]) => key);
    }

    private async _loadStageContentFromMiddleware(): Promise<void> {
        const missing = this._getMissingStageContentParams();

        if (missing.length > 0) {
            this.warn('roundtrip-stage-middleware-missing-params', { missing });
            return;
        }

        try {
            const request: RoundtripStageMiddlewareRequest = {
                brand: this.brand,
                tenant: this.tenant,
                lang: this.lang,
                currency: this.currency,
                identifier: this.identifier,
                travelcontext: this.travelContext,
                intent: this.intent,
                audience: this.facetAudience || undefined,
            };

            const responseData = await fetchRoundtripStageContent(request);

            this._stageContent = this._mapMiddlewareToStageContent(responseData);
            const hasHeaderContent = Boolean(
                this._stageContent.pageTitle || this._stageContent.componentTitle || this._stageContent.introTitle
            );
            this._introState = hasHeaderContent ? 'loaded' : 'empty';
        } catch (error) {
            this.error('roundtrip-stage-middleware-fetch-failed', {
                message: error instanceof Error ? error.message : 'Unknown middleware error',
            });
            this._backendFallbackPrice = '';
            this._backendDurationString = '';
            this._backendRoundtripTypes = '';
            this._backendRoundtripKey = '';
            this._backendOpportunityDestination = '';
            this._backendStartAndEndPoint = '';
            this._backendStartPoint = '';
            this._backendLanguages = '';
            this._backendGroupSize = '';
            this._backendBestTravelTime = '';
            this._stageContent = {
                pageTitle: '',
                componentTitle: '',
                introTitle: '',
            };
            this._introState = 'empty';
        }
    }

    /**
     * Registers an IntersectionObserver on the currently visible offer panel after the first render.
     * The sticky bar is only shown after the original offer panel has scrolled above the viewport.
     */
    override firstUpdated(): void {
        this._syncViewportLayout();
        this._observeActiveOfferPanel();
        this._observeGalleryOverlayState();
        window.addEventListener('resize', this._handleResize);
        window.addEventListener('scroll', this._handleScroll, { passive: true });
        window.addEventListener('touchmove', this._handleTouchMove, { passive: true });
        window.addEventListener('orientationchange', this._handleOrientationChange);
        document.addEventListener('visibilitychange', this._handleVisibilityChange);

        // Event-Listener für das Öffnen des Kalender-Modals TODO: nur wenn bestOffer da
        this.addEventListener('der-tm-offer-panel-price-box-action', this._handleOpenModal);
        this.addEventListener('der-tm-roundtrip-calendar-click-action', this._handleOfferPanelCalendarAction);
        this.addEventListener('confirmation-close', this._handleConfirmationClose);

        // iOS portal mode moves the sticky bar into Light DOM, so styles must exist globally.
        this._ensureStickyBarGlobalStyles();

        // iOS-specific polling fallback
        if (this._isIOS()) {
            this._iosStickyIntervalId = window.setInterval(() => {
                const prev = this._stickyVisible;
                this._updateStickyVisibility();
                if (prev !== this._stickyVisible) {
                    this._forceRepaintStickyBar();
                    this._updateStickyBarPortal();
                }
            }, 250);
        }
    }

    private _syncViewportLayout(): void {
        const isMobile = window.innerWidth < 768;
        if (this._isMobileViewport !== isMobile) {
            this._isMobileViewport = isMobile;
        }
    }

    /**
     * Disconnects observers and window listeners when the component is removed from the DOM.
     */
    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._removeSchemaEventInterceptor?.();
        this._removeSchemaEventInterceptor = null;
        this._ctaObserver?.disconnect();
        this._galleryOverlayObserver?.disconnect();
        this._clearObserveRetry();
        window.removeEventListener('resize', this._handleResize);
        window.removeEventListener('scroll', this._handleScroll);
        window.removeEventListener('touchmove', this._handleTouchMove);
        window.removeEventListener('orientationchange', this._handleOrientationChange);
        document.removeEventListener('visibilitychange', this._handleVisibilityChange);
        this.removeEventListener('der-tm-offer-panel-price-box-action', this._handleOpenModal);
        this.removeEventListener('der-tm-roundtrip-calendar-click-action', this._handleOfferPanelCalendarAction);
        this.removeEventListener('confirmation-close', this._handleConfirmationClose);

        if (this._iosStickyIntervalId != null) {
            window.clearInterval(this._iosStickyIntervalId);
            this._iosStickyIntervalId = null;
        }
        this._unlockBodyScroll();
        this._unmountStickyBarFromBody();
    }

    /**
     * Returns the selector for the offer panel container that is currently rendered for the viewport.
     * @returns CSS selector for the active offer panel wrapper
     */
    private _getActiveOfferPanelSelector(): string {
        const mobileSelector = '.der-tm-roundtrip-stage__mobile-offer-panel';
        const sidebarSelector = '.der-tm-roundtrip-stage__sidebar';

        const mobilePanel = this.renderRoot.querySelector<HTMLElement>(mobileSelector);
        const sidebarPanel = this.renderRoot.querySelector<HTMLElement>(sidebarSelector);

        const isVisible = (element: HTMLElement | null): boolean => {
            if (!element) return false;
            const styles = window.getComputedStyle(element);
            if (styles.display === 'none' || styles.visibility === 'hidden') return false;
            const rect = element.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        };

        if (isVisible(sidebarPanel)) {
            return sidebarSelector;
        }
        if (isVisible(mobilePanel)) {
            return mobileSelector;
        }

        // Fallback for first render before layout settles.
        return window.innerWidth < 768 ? mobileSelector : sidebarSelector;
    }

    private _clearObserveRetry(): void {
        if (this._observeRetryTimeoutId != null) {
            window.clearTimeout(this._observeRetryTimeoutId);
            this._observeRetryTimeoutId = null;
        }
    }

    private _isGalleryOverlayOpen(): boolean {
        const gallery = this.renderRoot.querySelector<HTMLElement>('der-tm-roundtrip-gallery');
        const overlay = gallery?.shadowRoot?.querySelector<HTMLElement>('der-tm-roundtrip-gallery-overlay');
        return overlay?.hasAttribute('open') ?? false;
    }

    private _observeGalleryOverlayState(): void {
        this._galleryOverlayObserver?.disconnect();
        this._galleryOverlayObserver = null;

        const gallery = this.renderRoot.querySelector<HTMLElement>('der-tm-roundtrip-gallery');
        const overlay = gallery?.shadowRoot?.querySelector<HTMLElement>('der-tm-roundtrip-gallery-overlay');
        if (!overlay) {
            window.setTimeout(() => this._observeGalleryOverlayState(), 150);
            return;
        }

        this._galleryOverlayObserver = new MutationObserver(() => {
            this._updateStickyVisibility();
        });
        this._galleryOverlayObserver.observe(overlay, {
            attributes: true,
            attributeFilter: ['open'],
        });

        this._updateStickyVisibility();
    }

    private _getPrimaryCtaButton(selector: string): HTMLElement | null {
        const panelWrapper = this.renderRoot.querySelector<HTMLElement>(selector);
        if (!panelWrapper) return null;

        const offerPanel = panelWrapper.querySelector<HTMLElement>('der-tm-roundtrip-offer-panel');
        const priceBox = offerPanel?.shadowRoot?.querySelector<HTMLElement>('der-tm-roundtrip-offer-panel-price-box');
        return priceBox?.querySelector<HTMLElement>('.m-price-box__button > der-ui-button:first-of-type') ?? null;
    }

    private _isPastActiveOfferCta(): boolean {
        const activeSelector = this._getActiveOfferPanelSelector();
        const primaryCtaButton = this._getPrimaryCtaButton(activeSelector);
        if (!primaryCtaButton) return false;

        const rect = primaryCtaButton.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return false;

        // Show sticky bar only when the button is fully above the viewport.
        return rect.bottom <= 0;
    }

    private _forceRepaintStickyBar(): void {
        const stickyBar = this.renderRoot.querySelector<HTMLElement>('.der-tm-roundtrip-stage__sticky-bar');
        if (stickyBar) {
            // Toggle a CSS property to force a repaint.
            stickyBar.style.transform = 'translateY(0px)';
            // Reset immediately after the forced repaint.
            setTimeout(() => {
                stickyBar.style.transform = '';
            }, 0);
        }
    }

    private _mountStickyBarToBody(): void {
        if (!this._shouldUseStickyBarPortal()) return;
        const stickyBar = this.renderRoot.querySelector<HTMLElement>('.der-tm-roundtrip-stage__sticky-bar');
        if (!stickyBar) return;
        // Safety net in case this method was not triggered in some lifecycle paths.
        this._ensureStickyBarGlobalStyles();
        // Move only when it should be visible and is not already in the body.
        if (this._stickyVisible && stickyBar.parentElement !== document.body) {
            document.body.appendChild(stickyBar);
            this._stickyBarPortalEl = stickyBar;
            // After moving: apply styles/class and force reflow.
            stickyBar.style.position = 'fixed';
            stickyBar.style.left = '0';
            stickyBar.style.right = '0';
            stickyBar.style.bottom = '0';
            stickyBar.style.width = '';
            stickyBar.style.boxSizing = 'border-box';
            stickyBar.style.zIndex = '2147483647'; // maximum z-index
            // Apply class and force reflow.
            window.requestAnimationFrame(() => {
                stickyBar.classList.add('is-visible');
                // Sticky bar moved into body, ensure correct z-index and position
            });
        } else if (!this._stickyVisible && stickyBar.parentElement === document.body) {
            // Remove visibility and move back after transition.
            stickyBar.classList.remove('is-visible');
            // Move back after transition.
            const transitionDuration = 400; // ms, with a small transition buffer
            setTimeout(() => {
                this._unmountStickyBarFromBody();
            }, transitionDuration);
        } else if (stickyBar.parentElement === document.body) {
            // Visible and already in body: ensure styles and z-index stay correct.
            stickyBar.style.position = 'fixed';
            stickyBar.style.left = '0';
            stickyBar.style.right = '0';
            stickyBar.style.bottom = '0';
            stickyBar.style.width = '';
            stickyBar.style.boxSizing = 'border-box';
            stickyBar.style.zIndex = '2147483647';
        }
    }

    private _unmountStickyBarFromBody(): void {
        if (this._stickyBarPortalEl && this._stickyBarPortalEl.parentElement === document.body) {
            // Move sticky bar back into the Shadow DOM
            const container = this.renderRoot.querySelector('.RoundtripStage-component');
            if (container) {
                container.appendChild(this._stickyBarPortalEl);
                // Sticky bar moved back from body to Shadow DOM
            }
            this._stickyBarPortalEl = null;
        }
    }

    private _updateStickyBarPortal(): void {
        if (!this._shouldUseStickyBarPortal()) return;
        this._mountStickyBarToBody();
    }

    private _updateStickyVisibility(): void {
        this._stickyVisible = this._isPastActiveOfferCta() && !this._isGalleryOverlayOpen();
        if (this._shouldUseStickyBarPortal()) {
            this._updateStickyBarPortal();
        }
    }

    /**
     * Observes the currently active offer panel CTA button and updates sticky visibility when it scrolls above the viewport.
     */
    private _observeActiveOfferPanel(): void {
        const selector = this._getActiveOfferPanelSelector();
        if (this._observedSelector === selector && this._ctaObserver && this._observeRetryTimeoutId == null) {
            return;
        }

        this._clearObserveRetry();
        this._ctaObserver?.disconnect();
        this._ctaObserver = null;
        this._observedSelector = selector;
        this._stickyVisible = false;

        const ctaTarget = this._getPrimaryCtaButton(selector);
        if (!ctaTarget) {
            this._observeRetryTimeoutId = window.setTimeout(() => {
                this._observeRetryTimeoutId = null;
                this._observeActiveOfferPanel();
            }, 150);
            return;
        }

        this._ctaObserver = new IntersectionObserver(
            () => {
                this._updateStickyVisibility();
            },
            {
                threshold: [0],
            }
        );

        this._ctaObserver.observe(ctaTarget);
    }

    /**
     * Emit a warning event
     * @param name - The warning name
     * @param data - Additional warning data
     */
    protected warn(name: string, data: object): void {
        emitWarning(this, name, data);
    }

    /**
     * Emit an error event
     * @param name - The error name
     * @param data - Additional error data
     */
    protected error(name: string, data: object): void {
        emitError(this, name, data);
    }

    /**
     * Emit a custom event with bubbling and composed enabled
     * @param name - The event name
     * @param detail - The event detail data
     */
    protected emitEvent(name: string, detail: unknown): void {
        emitEvent(this, name, detail);
    }

    private _getOfferPanelTemplateOptions(): OfferPanelTemplateOptions {
        return {
            identifier: this.identifier,
            lang: this.lang,
            brand: this.brand,
            currency: this.currency,
            tenant: this.tenant,
            componentTitle: this.componentTitle,
            fallbackPrice: this._backendFallbackPrice,
            durationString: this._backendDurationString,
            roundtripTypes: this._backendRoundtripTypes,
            roundtripId: this.identifier,
            roundtripKey: this._backendRoundtripKey,
            phoneNumber: this.phoneNumber,
            bookingDayOffset: this.bookingDayOffset,
            showOperator: this.showOperator,
            startAndEndPoint: this._backendStartAndEndPoint,
            startPoint: this._backendStartPoint,
            languages: this._backendLanguages,
            groupSize: this._backendGroupSize,
            bestTravelTime: this._backendBestTravelTime,
            bestOffer: null,
            additionalTrackingData: this.additionalTrackingData as Record<string, unknown>,
            facetAudience: this.facetAudience,
            facetStyle: this.facetStyle,
            facetMotive: this.facetMotive,
        };
    }

    private _getDialogTemplateOptions(): DialogTemplateOptions {
        return {
            modalContent: this._modalContent,
            bookingRequestVariant: this.bookingRequestVariant,
            offerRequestVariant: this.offerRequestVariant,
            offerRequestDescriptionOptional: this.offerRequestDescriptionOptional,
            newsletter: this.newsletter,
            enableRecap: this.enableRecap,
            identifier: this.identifier,
            productTitle: this._stageContent.pageTitle || this.componentTitle,
            lang: this.lang,
            brand: this.brand,
            currency: this.currency,
            tenant: this.tenant,
            componentTitle: this.componentTitle,
            fallbackPrice: this._backendFallbackPrice,
            durationString: this._backendDurationString,
            roundtripTypes: this._backendRoundtripTypes,
            roundtripId: this.identifier,
            roundtripKey: this._backendRoundtripKey,
            phoneNumber: this.phoneNumber,
            bookingDayOffset: this.bookingDayOffset,
            showOperator: this.showOperator,
            opportunityDestination: this._backendOpportunityDestination,
            startAndEndPoint: this._backendStartAndEndPoint,
            startPoint: this._backendStartPoint,
            languages: this._backendLanguages,
            groupSize: this._backendGroupSize,
            bestTravelTime: this._backendBestTravelTime,
            bestOffer: null,
            additionalTrackingData: this.additionalTrackingData as Record<string, unknown>,
            selectedOfferData: this._selectedOfferData,
            opportunityOwningTeam: this.opportunityOwningTeam,
            opportunityProductBrand: this.opportunityProductBrand,
            opportunitySource: this.opportunitySource,
            opportunityType: this.opportunityType,
            facetAudience: this.facetAudience,
            facetStyle: this.facetStyle,
            facetMotive: this.facetMotive,
            onBackClick: this._handleBackToCalendar,
            onCloseClick: this._handleCloseModal,
            onClose: this._handleDialogClose,
        };
    }

    /**
     * Renders a fully self-contained shimmer placeholder for the intro box.
     * @returns Template result for the loading-state placeholder
     */
    private _renderIntroSkeleton(): TemplateResult {
        return html`
            <div class="der-tm-rts-skeleton-header" aria-hidden="true">
                <div class="der-tm-rts-skeleton-title-block">
                    <div class="der-tm-rts-skeleton-page-title der-tm-rts-skeleton-fill"></div>
                    <div class="der-tm-rts-skeleton-component-title der-tm-rts-skeleton-fill"></div>
                </div>
                <div class="der-tm-rts-skeleton-intro-block">
                    <div class="der-tm-rts-skeleton-intro-line der-tm-rts-skeleton-fill"></div>
                    <div
                        class="der-tm-rts-skeleton-intro-line der-tm-rts-skeleton-intro-line--w-70 der-tm-rts-skeleton-fill"
                    ></div>
                </div>
            </div>
        `;
    }

    /**
     * Renders the roundtrip stage layout including the gallery, offer panels (mobile, sidebar, sticky), intro and rich text content.
     * Distributes stage-level attributes to the child MFEs (gallery and offer panels).
     * @returns Template result
     */
    protected render(): TemplateResult {
        const shouldRenderStickyBar = this._stickyVisible || this._shouldUseStickyBarPortal();

        return html`
            <div class="der-tm-roundtrip-stage__wrapper-component">
                <der-tm-roundtrip-gallery
                    lang=${this.lang}
                    brand=${this.brand}
                    tenant=${this.tenant}
                    currency=${this.currency}
                    identifier=${this.identifier}
                    image-count=${ifDefined(
                        this.galleryImageCount !== undefined ? String(this.galleryImageCount) : undefined
                    )}
                    facet_audience=${ifDefined(this.facetAudience)}
                    facet_style=${ifDefined(this.facetStyle)}
                    facet_motive=${ifDefined(this.facetMotive)}
                    .additionalTrackingData=${this.additionalTrackingData}
                ></der-tm-roundtrip-gallery>
                <div class="der-tm-roundtrip-stage__content-wrapper">
                    <div class="der-tm-roundtrip-stage__main-content">
                        <div class="der-tm-roundtrip-stage__skeleton-region ${this._introState}">
                            ${this._introState === 'loading'
                                ? this._renderIntroSkeleton()
                                : renderIntro(this._stageContent)}
                        </div>
                        ${this._isMobileViewport
                            ? html`<div class="der-tm-roundtrip-stage__mobile-offer-panel">
                                  ${renderOfferPanel(this._getOfferPanelTemplateOptions())}
                              </div>`
                            : html``}
                        ${renderRichText(this._stageContent)}
                    </div>
                    ${this._isMobileViewport
                        ? html``
                        : html`<div class="der-tm-roundtrip-stage__sidebar">
                              ${renderOfferPanel(this._getOfferPanelTemplateOptions())}
                          </div>`}
                </div>
                ${shouldRenderStickyBar
                    ? html`<div
                          class="der-tm-roundtrip-stage__sticky-bar ${this._stickyVisible ? 'is-visible' : ''}"
                          @der-tm-offer-panel-price-box-action=${this._handleStickyBarPriceBoxAction}
                          @der-tm-roundtrip-calendar-click-action=${this._handleStickyBarCalendarAction}
                      >
                          ${renderOfferPanel({
                              ...this._getOfferPanelTemplateOptions(),
                              variant: 'fixed',
                              active: this._stickyVisible,
                          })}
                      </div>`
                    : html``}
                ${renderDialog(this._getDialogTemplateOptions())}
            </div>
        `;
    }
}

if (!customElements.get('der-tm-roundtrip-stage')) {
    customElements.define('der-tm-roundtrip-stage', RoundtripStage);
}
